import { Trans } from "react-i18next";import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { ui } from "../../../components/ui";
import { LeaveActionToast } from "../../leave-management/components/LeaveActionToast";
import { getTenant } from "../../../services/tenantService";
import { getTenantServiceCatalogTree, saveTenantServiceLeafSelection } from "../../../services/tenantServicesService";
import AccessTree from "../components/AccessTree";

export function AssignTenantAccessPage() {
  const { id: tenantId } = useParams();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);

  // Full Services & Modules Catalog tree (Group -> Module -> SubModule ->
  // Feature), merged with this tenant's existing assignments. Every node
  // carries every leaf feature id nested under it, so checking a group
  // (e.g. "PSM") in the tree selects everything underneath it in one go,
  // while individual modules/sub-modules/features can still be toggled on
  // their own.
  const [tree, setTree] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Flat list of currently-selected leaf feature ids — this is the only
  // thing AccessTree needs, and the only thing we diff against on save.
  const [originalSelectedIds, setOriginalSelectedIds] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getTenant(tenantId)
    .then((data) => {
      if (!cancelled) setTenant(data);
    })
    .catch(() => {
      if (!cancelled) setTenant(null);
    })
    .finally(() => {
      if (!cancelled) setLoadingTenant(false);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    setLoadingServices(true);
    setLoadError(null);
    getTenantServiceCatalogTree(tenantId)
    .then(({ tree: catalogTree, selectedServiceIds: enabledIds }) => {
      if (cancelled) return;
      setTree(catalogTree);
      setOriginalSelectedIds(enabledIds);
      setSelectedServiceIds(enabledIds);
    })
    .catch((err) => {
      if (!cancelled) setLoadError(err?.message || "Failed to load services.");
    })
    .finally(() => {
      if (!cancelled) setLoadingServices(false);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const loading = loadingTenant || loadingServices;

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveTenantServiceLeafSelection(tenantId, originalSelectedIds, selectedServiceIds);
      setToast({ message: `Access selection saved for "${tenant?.tenantName || "tenant"}".`, type: "success" });
      navigate("/psm/config/tenants");
    } catch (err) {
      setToast({ message: err?.message || "Failed to save access. Please try again.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout overlay={<LeaveActionToast toast={toast} />}>
      <div className="mb-5">
        <PageBackLink to="/psm/config/tenants" />
        <h1 className={ui.pageTitle}>
          {loadingTenant ? "Manage Access" : tenant?.tenantName || tenant?.name || "Tenant"}
        </h1>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 p-5 sm:p-6 max-w-[640px]">
        {!loading && loadError &&
        <p className="text-sm text-[var(--color-error)] bg-[var(--color-error)]/10 border border-[var(--color-error)]/25 rounded-xl px-3.5 py-2.5 mb-4">
            {loadError}
          </p>
        }

        {loading &&
        <p className="text-sm text-[var(--color-text-secondary)] text-center py-6"><Trans i18nKey="tenantManagement.loadingServices" /></p>
        }

        {!loading && !loadError &&
        <div className="max-h-[520px] overflow-y-auto pr-1">
            <AccessTree
            data={tree}
            mode="select"
            selectedServiceIds={selectedServiceIds}
            onChange={setSelectedServiceIds} />
          
          </div>
        }

        <div className="flex justify-end gap-2.5 mt-5 pt-4 border-t border-[var(--color-text-border)]/70">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/psm/config/tenants")}
            disabled={saving}
            className="h-10 px-4 rounded-xl">
            <Trans i18nKey="formActions.cancel" />
          
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="h-10 px-4 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)]">
            
            {saving ? "Saving..." : "Save access"}
          </Button>
        </div>
      </div>
    </AppLayout>);

}
